/**
 * @module components/InvoiceStatusBadge
 * @description Displays invoice status as a styled badge with appropriate tone
 */

import React, { useMemo } from 'react';
import { SanityBadge } from 'src/components/SanityBadge';
import { Assets } from 'src/types/assets';
import { Invoices } from 'src/types/invoices';
import { Sanity } from 'src/types/sanity';
import * as Styles from './styles';
import { InvoiceStatusBadgeProps } from './types';

/**
 * @func getInvoiceStatusTone
 * @summary Determine Sanity tone color based on invoice status
 * @param {string} status Invoice status
 * @param {boolean} paid Invoice paid flag
 * @returns {Sanity.Types.SanityTone} Sanity tone for badge
 */
const getInvoiceStatusTone = (status?: string, paid?: boolean): Sanity.Types.SanityTone => {
  if (paid || status === Invoices.Types.InvoiceStatus.PAID) {
    return Sanity.Types.SanityTone.POSITIVE;
  }
  if (status === Invoices.Types.InvoiceStatus.VOIDED || status === Invoices.Types.InvoiceStatus.DRAFT) {
    return Sanity.Types.SanityTone.CRITICAL;
  }
  if (status === Invoices.Types.InvoiceStatus.PAST_DUE) {
    return Sanity.Types.SanityTone.CAUTION;
    // return Sanity.Types.SanityTone.CRITICAL;
  }
  return Sanity.Types.SanityTone.PRIMARY;
};

/**
 * @component InvoiceStatusBadge
 * @summary Renders invoice status badge with tone-based styling
 * Automatically derives tone and label from invoice data unless overridden
 * @type {React.FC<InvoiceStatusBadgeProps>}
 * @param {InvoiceStatusBadgeProps} props - Component props from types.ts
 * @returns {React.ReactElement} Styled status badge
 */
export const InvoiceStatusBadge: React.FC<InvoiceStatusBadgeProps> = ({
  invoice,
  tone: toneProp,
  label: labelProp,
  className,
  children,
  ...rest
}) => {
  const computedTone = useMemo(
    () => toneProp ?? getInvoiceStatusTone(invoice.status, invoice.paid),
    [toneProp, invoice.status, invoice.paid],
  );

  const computedLabel = useMemo(() => {
    if (labelProp) return labelProp;
    if (invoice.status) {
      return Invoices.Types.InvoiceStatusLabels[invoice.status as Invoices.Types.InvoiceStatus] || invoice.status;
    }
    return 'Unknown';
  }, [labelProp, invoice.status]);

  return (
    <Styles.Container className={className} {...rest}>
      <SanityBadge
        tone={computedTone}
        outline
        icon={(invoice.status === Invoices.Types.InvoiceStatus.PAID && invoice.paid)
          ? (
            Assets.Data.ExpiryStatusIcons[Assets.Types.ExpiryStatus.NORMAL]
          ) : (
            Assets.Data.ExpiryStatusIcons[Assets.Types.ExpiryStatus.WARNING]
          )}
      >
        {children ?? computedLabel}
      </SanityBadge>
    </Styles.Container>
  );
};

export default InvoiceStatusBadge;
